import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { API_POST, API_PATCH, DBRequest, useConfig } from '../../api/api';
import Loader from '../special/Loader';
import Feedback from '../special/Feedback';
import WebFormFieldBuilder from '../web-forms/WebFormFieldBuilder';
import { mappingOptionsFor, requiredMappingGroups } from '../web-forms/mappingOptions';

const LEAD_TYPES = [
  { value: 'person', label: 'Person' },
  { value: 'company', label: 'Firma' },
  { value: 'both', label: 'Firma + Person' },
];

function withUids(fields = []) {
  return fields.map((f, i) => ({
    ...f,
    uid: f.uid || `f_${i}_${Math.round(Math.random() * 1e6)}`,
    options: f.options || [],
    width: f.width || 'full',
    required: Boolean(f.required),
    mapping: f.mapping || 'none',
  }));
}

function stripForApi(fields = []) {
  return fields.map(({ uid, ...rest }) => rest);
}

const SectionTitle = ({ children }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  return (
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: colors.text, opacity: 0.7, fontWeight: 600 }}>
      {children}
    </Typography>
  );
};

const NewWebForm = ({ onClose, onCreate = () => {}, startData }) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [alertState, setAlertState] = useState({ alertOpen: false, alertType: 'info', alertText: '' });

  const isEditing = Boolean(startData?.id);

  const [form, setForm] = useState(() => ({
    name: startData?.name || '',
    description: startData?.description || '',
    leadType: startData?.leadType || 'person',
    isActive: startData?.isActive ?? true,
    dedupeEnabled: startData?.dedupeEnabled ?? true,
    fields: withUids(startData?.fields),
    notificationRecipients: (startData?.notificationRecipients || []).join('\n'),
    notificationSubject: startData?.notificationSubject || '',
    notificationBody: startData?.notificationBody || '',
    autoresponderEnabled: startData?.autoresponderEnabled ?? false,
    autoresponderEmailField: startData?.autoresponderEmailField || '',
    autoresponderSubject: startData?.autoresponderSubject || '',
    autoresponderBody: startData?.autoresponderBody || '',
    consentRequired: startData?.consentRequired ?? false,
    consentText: startData?.consentText || '',
    successMessage: startData?.successMessage || '',
    submitLabel: startData?.submitLabel || '',
  }));

  function patch(changes) {
    setForm((f) => ({ ...f, ...changes }));
  }

  // When the lead type changes, drop field mappings that are no longer valid for it,
  // so saving cannot fail with a confusing server-side 422.
  function handleLeadTypeChange(newType) {
    const allowed = new Set(mappingOptionsFor(newType).map((o) => o.value));
    setForm((f) => ({
      ...f,
      leadType: newType,
      fields: f.fields.map((field) =>
        allowed.has(field.mapping) ? field : { ...field, mapping: 'none' }
      ),
    }));
  }

  // Prune a stale autoresponder field reference if that field was removed/changed.
  useEffect(() => {
    const opts = form.fields.filter((f) => f.key && (f.type === 'email' || f.mapping?.endsWith('.email')));
    if (form.autoresponderEmailField && !opts.some((f) => f.key === form.autoresponderEmailField)) {
      patch({ autoresponderEmailField: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.fields]);

  const placeholderHint = useMemo(() => {
    const keys = form.fields.filter((f) => f.key).map((f) => `%%${f.key}%%`);
    return [...keys, '%%all_fields%%', '%%form_name%%', '%%submitted_at%%'].join('  ');
  }, [form.fields]);

  const emailFieldOptions = useMemo(
    () => form.fields.filter((f) => f.key && (f.type === 'email' || f.mapping?.endsWith('.email'))),
    [form.fields]
  );

  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  function handleSubmit() {
    // Client-side guards mirroring the backend rules — fail fast with clear feedback.
    const keys = form.fields.map((f) => f.key);
    if (keys.some((k) => !k)) {
      handleFeedback(t('Every field needs a key.'), 'error');
      return;
    }
    if (new Set(keys).size !== keys.length) {
      handleFeedback(t('Field keys must be unique.'), 'error');
      return;
    }
    const usedRequiredMappings = form.fields
      .filter((f) => f.required && f.mapping && f.mapping !== 'none')
      .map((f) => f.mapping);
    const missing = requiredMappingGroups(form.leadType).some(
      (group) => !group.some((m) => usedRequiredMappings.includes(m))
    );
    if (missing) {
      handleFeedback(t('Map a required field to the lead name (and company name for company forms).'), 'error');
      return;
    }

    const payload = {
      name: form.name,
      description: form.description,
      leadType: form.leadType,
      isActive: form.isActive,
      dedupeEnabled: form.dedupeEnabled,
      fields: stripForApi(form.fields),
      notificationRecipients: form.notificationRecipients
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean),
      notificationSubject: form.notificationSubject,
      notificationBody: form.notificationBody,
      autoresponderEnabled: form.autoresponderEnabled,
      autoresponderEmailField: form.autoresponderEmailField || null,
      autoresponderSubject: form.autoresponderSubject,
      autoresponderBody: form.autoresponderBody,
      consentRequired: form.consentRequired,
      consentText: form.consentText,
      successMessage: form.successMessage,
      submitLabel: form.submitLabel,
    };

    DBRequest({
      config,
      path: isEditing ? `web-forms/${startData.id}` : 'web-forms',
      method: isEditing ? API_PATCH(payload) : API_POST(payload),
      onResponse: (res) => {
        onCreate(res.data);
        onClose();
      },
      onFeedback: handleFeedback,
      onLoading: setIsLoading,
    });
  }

  return (
    <Box className="p-2 h-full overflow-y-auto" sx={{ width: '760px', maxWidth: '100%' }}>
      <Loader active={isLoading} />
      <Typography variant="h6" sx={{ mb: 1 }}>
        {isEditing ? t('Edit Form') : t('New Form')}
      </Typography>

      <SectionTitle>{t('Basics')}</SectionTitle>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <TextField
          size="small"
          label={t('Name')}
          value={form.name}
          onChange={(e) => patch({ name: e.target.value })}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <TextField
          size="small"
          select
          label={t('Lead type')}
          value={form.leadType}
          onChange={(e) => handleLeadTypeChange(e.target.value)}
          sx={{ width: 200 }}
        >
          {LEAD_TYPES.map((lt) => (
            <MenuItem key={lt.value} value={lt.value}>{t(lt.label)}</MenuItem>
          ))}
        </TextField>
      </Box>
      <TextField
        size="small"
        label={`${t('Description')} (${t('optional')})`}
        value={form.description}
        onChange={(e) => patch({ description: e.target.value })}
        fullWidth
        multiline
        sx={{ mt: 1.5 }}
      />
      <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={(e) => patch({ isActive: e.target.checked })} />}
          label={t('Active')}
        />
        <FormControlLabel
          control={<Switch checked={form.dedupeEnabled} onChange={(e) => patch({ dedupeEnabled: e.target.checked })} />}
          label={t('Detect duplicate leads')}
        />
      </Box>

      <Divider sx={{ my: 2 }} />
      <SectionTitle>{t('Form fields')}</SectionTitle>
      <WebFormFieldBuilder
        fields={form.fields}
        leadType={form.leadType}
        onChange={(fields) => patch({ fields })}
      />

      <Divider sx={{ my: 2 }} />
      <SectionTitle>{t('Notification e-mail')}</SectionTitle>
      <TextField
        size="small"
        label={t('Recipients (one per line)')}
        value={form.notificationRecipients}
        onChange={(e) => patch({ notificationRecipients: e.target.value })}
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        size="small"
        label={t('Subject')}
        value={form.notificationSubject}
        onChange={(e) => patch({ notificationSubject: e.target.value })}
        fullWidth
        sx={{ mt: 1.5 }}
      />
      <TextField
        size="small"
        label={t('Body')}
        value={form.notificationBody}
        onChange={(e) => patch({ notificationBody: e.target.value })}
        fullWidth
        multiline
        minRows={4}
        sx={{ mt: 1.5 }}
      />
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>
        {t('Available placeholders')}: {placeholderHint}
      </Typography>

      <Divider sx={{ my: 2 }} />
      <SectionTitle>{t('Autoresponder')}</SectionTitle>
      <FormControlLabel
        control={<Switch checked={form.autoresponderEnabled} onChange={(e) => patch({ autoresponderEnabled: e.target.checked })} />}
        label={t('Send confirmation to submitter')}
      />
      {form.autoresponderEnabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <TextField
            size="small"
            select
            label={t('Submitter e-mail field')}
            value={form.autoresponderEmailField}
            onChange={(e) => patch({ autoresponderEmailField: e.target.value })}
            sx={{ width: 280 }}
          >
            {emailFieldOptions.length === 0 && (
              <MenuItem value="" disabled>{t('Add an e-mail field first')}</MenuItem>
            )}
            {emailFieldOptions.map((f) => (
              <MenuItem key={f.uid} value={f.key}>{f.label || f.key}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={t('Subject')}
            value={form.autoresponderSubject}
            onChange={(e) => patch({ autoresponderSubject: e.target.value })}
            fullWidth
          />
          <TextField
            size="small"
            label={t('Body')}
            value={form.autoresponderBody}
            onChange={(e) => patch({ autoresponderBody: e.target.value })}
            fullWidth
            multiline
            minRows={3}
          />
        </Box>
      )}

      <Divider sx={{ my: 2 }} />
      <SectionTitle>{t('Options')}</SectionTitle>
      <FormControlLabel
        control={<Switch checked={form.consentRequired} onChange={(e) => patch({ consentRequired: e.target.checked })} />}
        label={t('Require privacy consent')}
      />
      {form.consentRequired && (
        <TextField
          size="small"
          label={t('Consent text')}
          value={form.consentText}
          onChange={(e) => patch({ consentText: e.target.value })}
          fullWidth
          multiline
          sx={{ mt: 1 }}
        />
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
        <TextField
          size="small"
          label={t('Submit button label')}
          value={form.submitLabel}
          onChange={(e) => patch({ submitLabel: e.target.value })}
          sx={{ width: 240 }}
        />
        <TextField
          size="small"
          label={t('Success message')}
          value={form.successMessage}
          onChange={(e) => patch({ successMessage: e.target.value })}
          sx={{ flex: 1, minWidth: 240 }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('Cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} sx={{ textTransform: 'none' }}>
          {isEditing ? t('Save') : t('Create')}
        </Button>
      </Box>

      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

export default NewWebForm;
